import { useEffect, useRef, useState } from "react";
import { AppDownloadEmailModal } from "Components/molecules/AppDownloadEmailModal";
import { useAuth } from "shared/contexts/AuthContext";
import {
  type AppStore,
  openStore,
  registerAppDownloadHandler,
} from "shared/services/appDownload";
import { sendGTMEvent } from "shared/services/GTM";
import { getPlatformType } from "shared/utils/platform";
import { getUserKey } from "shared/utils/userKey";
import { ThemeProvider } from "styled-components";
import { theme } from "styles/theme";

type EmailSource = "session" | "modal";

function pushSignup(userKey: string, emailSource: EmailSource) {
  sendGTMEvent("advancedbr_signup", {
    platform: getPlatformType(),
    user_key: userKey,
    email_source: emailSource,
  });
}

/**
 * Envia `advancedbr_signup` e abre a loja, os dois no mesmo clique:
 * - com o hash já calculado, o evento sai antes do `window.open`, porque no
 *   iPhone a App Store tira o Safari da frente logo que abre;
 * - sem o hash, a loja abre primeiro e o evento vai quando o hash ficar
 *   pronto, já que um `window.open` depois de um `await` é bloqueado.
 */
function signupAndOpenStore(
  store: AppStore,
  email: string,
  readyKey: string | null,
  emailSource: EmailSource,
) {
  if (readyKey) {
    pushSignup(readyKey, emailSource);
    openStore(store);
    return;
  }

  openStore(store);
  void getUserKey(email).then((userKey) => {
    if (userKey) pushSignup(userKey, emailSource);
  });
}

/**
 * Botões de download do app em visitas da campanha AdvancedBR: com sessão,
 * usa o e-mail da conta; sem sessão, pede o e-mail numa modal.
 */
export function AppDownloadGate() {
  const { user } = useAuth();
  const sessionEmail = user?.email;
  const [sessionKey, setSessionKey] = useState<string | null>(null);
  const [pendingStore, setPendingStore] = useState<AppStore | null>(null);
  const typedKey = useRef<{ email: string; key: string | null } | null>(null);

  useEffect(() => {
    let active = true;
    setSessionKey(null);
    if (sessionEmail) {
      void getUserKey(sessionEmail).then((key) => {
        if (active) setSessionKey(key);
      });
    }
    return () => {
      active = false;
    };
  }, [sessionEmail]);

  useEffect(
    () =>
      registerAppDownloadHandler((store) => {
        if (sessionEmail) {
          signupAndOpenStore(store, sessionEmail, sessionKey, "session");
          return;
        }
        setPendingStore(store);
      }),
    [sessionEmail, sessionKey],
  );

  // O gate fica no App, fora das páginas que aplicam o tema.
  return (
    <ThemeProvider theme={theme}>
      <AppDownloadEmailModal
        isOpen={pendingStore !== null}
        onEmailChange={(email) => {
          typedKey.current = { email, key: null };
          void getUserKey(email).then((key) => {
            if (typedKey.current?.email === email) {
              typedKey.current = { email, key };
            }
          });
        }}
        onSubmit={(email) => {
          if (!pendingStore) return;
          const readyKey =
            typedKey.current?.email === email ? typedKey.current.key : null;
          signupAndOpenStore(pendingStore, email, readyKey, "modal");
          setPendingStore(null);
        }}
        onClose={() => setPendingStore(null)}
      />
    </ThemeProvider>
  );
}

export default AppDownloadGate;
